<template>
  <div class="nano-dashboard">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div>
        <h1 class="mb-1 font-regular">Loan Operations</h1>
        <p class="text-muted mb-0">Nanoloan portfolio, collections, and income sweep</p>
      </div>
      <div class="d-flex flex-wrap align-items-center nano-toolbar">
        <input
          type="text"
          class="form-control nano-search"
          v-model="search"
          placeholder="Search loan ID, borrower name, phone, BVN..."
          @keyup.enter="loadLoans(1)"
        />
        <div class="nano-range-label">
          {{ rangeLabel }}
        </div>
        <div class="btn-group nano-range-btns" role="group">
          <button
            v-for="opt in rangeOptions"
            :key="opt.value"
            type="button"
            class="btn btn-sm"
            :class="range === opt.value && !customMode ? 'btn-primary' : 'btn-outline-secondary'"
            @click="setRange(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <div class="d-flex align-items-center nano-custom-dates">
          <input type="date" class="form-control form-control-sm" v-model="customFrom" />
          <span class="mx-1 text-muted">→</span>
          <input type="date" class="form-control form-control-sm" v-model="customTo" />
          <button
            type="button"
            class="btn btn-sm btn-outline-primary ml-2"
            :disabled="!customFrom || !customTo"
            @click="applyCustomRange"
          >
            Apply
          </button>
        </div>
      </div>
    </div>

    <div v-if="dashboardLoading" class="text-center py-5 text-muted">
      Loading dashboard...
    </div>

    <template v-else-if="dashboard">
      <!-- KPI strip -->
      <div class="row">
        <div
          class="col-xl-3 col-lg-4 col-md-6 mb-3"
          v-for="card in kpiCards"
          :key="card.key"
        >
          <button
            type="button"
            class="nano-kpi-card w-100 text-left"
            :class="{ active: activeKpiFilter === card.filter }"
            @click="applyKpiFilter(card.filter)"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="nano-kpi-label">{{ card.label }}</div>
                <div class="nano-kpi-value">{{ card.value }}</div>
                <div class="nano-kpi-sub" v-if="card.sub">{{ card.sub }}</div>
              </div>
              <span class="nano-kpi-icon">{{ card.icon }}</span>
            </div>
            <div
              class="nano-trend mt-2"
              :class="card.trend >= 0 ? 'up' : 'down'"
              v-if="card.trend !== null && card.trend !== undefined"
            >
              {{ card.trend >= 0 ? "+" : "" }}{{ card.trend }}% vs prior period
            </div>
          </button>
        </div>
      </div>

      <!-- Insight row -->
      <div class="row">
        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow nano-panel h-100">
            <div class="card-body">
              <h5 class="mb-3">Applications In This Period</h5>
              <div class="nano-lifecycle">
                <button
                  type="button"
                  class="nano-life-pill"
                  v-for="stage in lifecycleStages"
                  :key="stage.key"
                  @click="applyStatusFilter(stage.status)"
                >
                  <span class="name">{{ stage.label }}</span>
                  <span class="count">{{ stage.count }}</span>
                  <span class="pct">{{ stage.pct }}%</span>
                </button>
              </div>
              <div class="row mt-3">
                <div
                  class="col-6 mb-2"
                  v-for="ref in refusalBoxes"
                  :key="ref.key"
                >
                  <button
                    type="button"
                    class="nano-refuse-box w-100"
                    @click="applyStatusFilter(ref.status)"
                  >
                    <div class="label">{{ ref.label }}</div>
                    <div class="value">{{ ref.count }}</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow nano-panel h-100">
            <div class="card-body">
              <h5 class="mb-3">Fell Due In This Period</h5>
              <div
                class="nano-aging-row"
                v-for="bucket in agingBuckets"
                :key="bucket.key"
                @click="applyAgingFilter(bucket.key)"
              >
                <div class="d-flex justify-content-between mb-1">
                  <span>{{ bucket.label }}</span>
                  <strong>{{ formatMoney(bucket.amount) }}</strong>
                </div>
                <div class="nano-bar-track">
                  <div
                    class="nano-bar-fill"
                    :style="{
                      width: agingBarWidth(bucket.amount) + '%',
                      background: bucket.color
                    }"
                  ></div>
                </div>
                <div class="text-muted small mt-1">{{ bucket.count }} loans</div>
              </div>
              <div class="row mt-3 nano-aging-stats">
                <div class="col-4">
                  <div class="label">Loans overdue</div>
                  <div class="value">{{ dashboard.overdue_aging.loans_overdue }}</div>
                </div>
                <div class="col-4">
                  <div class="label">Avg balance</div>
                  <div class="value">{{ formatMoney(dashboard.overdue_aging.avg_overdue_balance) }}</div>
                </div>
                <div class="col-4">
                  <div class="label">Avg age</div>
                  <div class="value">{{ dashboard.overdue_aging.avg_overdue_age_days }}d</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow nano-panel h-100">
            <div class="card-body">
              <h5 class="mb-3">Income Sweep</h5>
              <p class="text-muted small mb-3">Loans completed in this period</p>
              <div class="mb-4">
                <div class="d-flex justify-content-between">
                  <span>Interest Sweep</span>
                  <strong>{{ dashboard.income_sweep.interest_pct }}%</strong>
                </div>
                <div class="nano-bar-track mt-1">
                  <div
                    class="nano-bar-fill"
                    :style="{ width: dashboard.income_sweep.interest_pct + '%', background: '#0d6efd' }"
                  ></div>
                </div>
                <div class="small text-muted mt-1">
                  {{ formatMoney(dashboard.income_sweep.interest_swept) }} /
                  {{ formatMoney(dashboard.income_sweep.interest_eligible) }}
                  · Pending {{ formatMoney(dashboard.income_sweep.interest_pending) }}
                </div>
                <div class="small text-muted" v-if="dashboard.income_sweep.last_interest_transfer_at">
                  Last transfer: {{ dashboard.income_sweep.last_interest_transfer_at | date_two }}
                </div>
              </div>
              <div>
                <div class="d-flex justify-content-between">
                  <span>Charges Sweep</span>
                  <strong>{{ dashboard.income_sweep.charges_pct }}%</strong>
                </div>
                <div class="nano-bar-track mt-1">
                  <div
                    class="nano-bar-fill"
                    :style="{ width: dashboard.income_sweep.charges_pct + '%', background: '#198754' }"
                  ></div>
                </div>
                <div class="small text-muted mt-1">
                  {{ formatMoney(dashboard.income_sweep.charges_swept) }} /
                  {{ formatMoney(dashboard.income_sweep.charges_eligible) }}
                  · Pending {{ formatMoney(dashboard.income_sweep.charges_pending) }}
                </div>
                <div class="small text-muted" v-if="dashboard.income_sweep.last_charges_transfer_at">
                  Last transfer: {{ dashboard.income_sweep.last_charges_transfer_at | date_two }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Officers -->
      <div class="row">
        <div class="col-lg-6 mb-3">
          <div class="card card--no-shadow nano-panel">
            <div class="card-body">
              <h5 class="mb-3">Review Officers</h5>
              <p class="text-muted small mb-3">Reviews dated in this period</p>
              <CDataTable
                :items="dashboard.review_officers"
                :fields="reviewFields"
                :itemsPerPage="5"
                sorter
              >
                <template #rank="{ item, index }">
                  <td>{{ index + 1 }}</td>
                </template>
                <template #approval_rate="{ item }">
                  <td class="text-success font-weight-bold">{{ item.approval_rate }}%</td>
                </template>
              </CDataTable>
            </div>
          </div>
        </div>
        <div class="col-lg-6 mb-3">
          <div class="card card--no-shadow nano-panel">
            <div class="card-body">
              <h5 class="mb-3">Collection Officers</h5>
              <p class="text-muted small mb-3">Collected in this period, against loans due in this period</p>
              <CDataTable
                :items="dashboard.collection_officers"
                :fields="collectionFields"
                :itemsPerPage="5"
                sorter
              >
                <template #rank="{ item, index }">
                  <td>{{ index + 1 }}</td>
                </template>
                <template #amount_collected="{ item }">
                  <td>{{ formatMoney(item.amount_collected) }}</td>
                </template>
                <template #collection_rate="{ item }">
                  <td class="text-success font-weight-bold">{{ item.collection_rate }}%</td>
                </template>
              </CDataTable>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Loans table -->
    <div class="card card--no-shadow nano-panel">
      <div class="card-body">
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Loans ({{ loanTotalDisplay }})</h5>
          <div class="d-flex flex-wrap nano-table-filters">
            <select class="form-control form-control-sm" v-model="status" @change="loadLoans(1)">
              <option value="">Status</option>
              <option value="pending">Pending</option>
              <option value="awaiting-disbursement">Awaiting Disbursement</option>
              <option value="active">Active</option>
              <option value="overdue">Overdue</option>
              <option value="overdue-by-3-days">Overdue By 3 Days</option>
              <option value="overdue-by-7-days">Overdue By 7 Days</option>
              <option value="completed">Completed</option>
              <option value="refused">Refused</option>
            </select>
            <button class="btn btn-sm btn-outline-secondary" @click="resetFilters">Reset</button>
            <button class="btn btn-sm btn-primary" @click="loadLoans(1)" :disabled="loansLoading">
              {{ loansLoading ? "Loading..." : "Refresh" }}
            </button>
          </div>
        </div>

        <CDataTable :items="loans" :fields="loanFields" sorter :itemsPerPage="15">
          <template #instant_reference="{ item }">
            <td>
              <router-link
                v-if="permissions.includes('view-instant-loan')"
                :to="{ name: 'LoanComponent', params: { slug: item.instant_reference } }"
              >
                {{ item.instant_reference }}
              </router-link>
              <span v-else>{{ item.instant_reference }}</span>
            </td>
          </template>
          <template #borrower="{ item }">
            <td>
              {{ item.user ? `${item.user.first_name} ${item.user.last_name}` : "-" }}
            </td>
          </template>
          <template #phone="{ item }">
            <td>{{ item.user ? item.user.phone_number : "-" }}</td>
          </template>
          <template #approved_loan_amount="{ item }">
            <td>{{ formatMoney(item.approved_loan_amount) }}</td>
          </template>
          <template #remaining_balance="{ item }">
            <td>{{ formatMoney(item.remaining_balance) }}</td>
          </template>
          <template #loan_status="{ item }">
            <td>
              <span class="nano-status" :class="statusClass(item.loan_status)">
                {{ item.loan_status }}
              </span>
            </td>
          </template>
          <template #due_date="{ item }">
            <td>
              <div>{{ item.due_date | date }}</div>
              <small :class="item.loan_status === 'overdue' ? 'text-danger' : 'text-muted'">
                {{ dueHint(item) }}
              </small>
            </td>
          </template>
          <template #aging="{ item }">
            <td>{{ agingLabel(item) }}</td>
          </template>
          <template #collection_officer="{ item }">
            <td>
              <span v-if="item.collection_user_details">
                {{ item.collection_user_details.first_name }}
                {{ item.collection_user_details.last_name }}
              </span>
              <span v-else class="text-muted">Unassigned</span>
            </td>
          </template>
          <template #review_officer="{ item }">
            <td>
              <span v-if="item.assigned_review_officer">
                {{ item.assigned_review_officer.first_name }}
                {{ item.assigned_review_officer.last_name }}
              </span>
              <span v-else-if="item.review_user_details">
                {{ item.review_user_details.first_name }}
                {{ item.review_user_details.last_name }}
              </span>
              <span v-else class="text-muted">Unassigned</span>
            </td>
          </template>
          <template #created_at="{ item }">
            <td>{{ item.created_at | date_two }}</td>
          </template>
          <template #action="{ item }">
            <td>
              <router-link
                v-if="permissions.includes('view-instant-loan')"
                class="btn btn-sm btn-primary"
                :to="{ name: 'LoanComponent', params: { slug: item.instant_reference } }"
              >
                View
              </router-link>
            </td>
          </template>
        </CDataTable>

        <div class="d-flex justify-content-end mt-3" v-if="lastPage > 1">
          <CPagination
            :pages="lastPage"
            :activePage.sync="currentPage"
            @update:activePage="onPageChange"
          ></CPagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/services/api.service";
import CDataTable from "@/components/CDataTable.vue";
import CPagination from "@/components/CPagination.vue";

const reviewFields = [
  { key: "rank", label: "#" },
  { key: "name", label: "Officer" },
  { key: "loans_reviewed", label: "Loans Reviewed" },
  { key: "approved", label: "Approved" },
  { key: "approval_rate", label: "Approval Rate" }
];

const collectionFields = [
  { key: "rank", label: "#" },
  { key: "name", label: "Officer" },
  { key: "loans_assigned", label: "Loans Assigned" },
  { key: "amount_collected", label: "Amount Collected" },
  { key: "collection_rate", label: "Collection Rate" }
];

const loanFields = [
  { key: "instant_reference", label: "Loan ID" },
  { key: "borrower", label: "Borrower" },
  { key: "phone", label: "Phone" },
  { key: "approved_loan_amount", label: "Amount" },
  { key: "remaining_balance", label: "Outstanding" },
  { key: "loan_status", label: "Status" },
  { key: "due_date", label: "Due Date" },
  { key: "aging", label: "Aging" },
  { key: "collection_officer", label: "Collection Officer" },
  { key: "review_officer", label: "Review Officer" },
  { key: "created_at", label: "Created At" },
  { key: "action", label: "Actions" }
];

export default {
  name: "NanoLoanDashboard",
  components: {
    CDataTable,
    CPagination
  },
  data() {
    return {
      dashboardLoading: false,
      loansLoading: false,
      dashboard: null,
      range: "30d",
      customMode: false,
      customFrom: "",
      customTo: "",
      search: "",
      status: "",
      activeKpiFilter: "",
      loans: [],
      currentPage: 1,
      lastPage: 1,
      loanCount: 0,
      reviewFields,
      collectionFields,
      loanFields,
      rangeOptions: [
        { label: "Today", value: "today" },
        { label: "7D", value: "7d" },
        { label: "15D", value: "15d" },
        { label: "30D", value: "30d" },
        { label: "90D", value: "90d" }
      ]
    };
  },
  computed: {
    permissions() {
      return this.$store.getters.permissions || [];
    },
    rangeLabel() {
      if (!this.dashboard || !this.dashboard.range) return "";
      return `${this.dashboard.range.from} → ${this.dashboard.range.to}`;
    },
    loanTotalDisplay() {
      return this.loanCount || (this.loans ? this.loans.length : 0);
    },
    kpiCards() {
      if (!this.dashboard) return [];
      const k = this.dashboard.kpis;
      const t = k.trends || {};
      return [
        {
          key: "portfolio_value",
          label: "Funded From Applications",
          value: this.formatMoney(k.portfolio_value),
          sub: "Applied in this period and disbursed by period end",
          trend: t.portfolio_value,
          icon: "▣",
          filter: ""
        },
        {
          key: "active_loans",
          label: "Still Active",
          value: this.formatNumber(k.active_loans),
          sub: "Disbursed in this period",
          trend: t.active_loans,
          icon: "◎",
          filter: "active"
        },
        {
          key: "outstanding_balance",
          label: "Still Outstanding",
          value: this.formatMoney(k.outstanding_balance),
          sub: "Disbursed in this period, not yet cleared",
          trend: t.outstanding_balance,
          icon: "◷",
          filter: ""
        },
        {
          key: "overdue_balance",
          label: "Still Unpaid",
          value: this.formatMoney(k.overdue_balance),
          sub: "Due in this period and still overdue",
          trend: t.overdue_balance,
          icon: "⚠",
          filter: "overdue"
        },
        {
          key: "disbursed_amount",
          label: "Disbursed",
          value: this.formatMoney(k.disbursed_amount),
          sub: `${this.formatNumber(k.disbursed_count)} loans disbursed in this period`,
          trend: t.disbursed_amount,
          icon: "➤",
          filter: ""
        },
        {
          key: "collection_rate",
          label: "Collected On Due Loans",
          value: `${k.collection_rate}%`,
          trend: t.collection_rate,
          icon: "↻",
          filter: ""
        },
        {
          key: "penalties_accrued",
          label: "Penalties Booked",
          value: this.formatMoney(k.penalties_accrued),
          trend: t.penalties_accrued,
          icon: "💳",
          filter: "overdue"
        },
        {
          key: "repayment_success_rate",
          label: "Repayment Success",
          value: `${k.repayment_success_rate}%`,
          trend: t.repayment_success_rate,
          icon: "✓",
          filter: "completed"
        }
      ];
    },
    lifecycleStages() {
      if (!this.dashboard) return [];
      const l = this.dashboard.lifecycle;
      return [
        { key: "pending", label: "Pending", count: l.pending, pct: l.pending_pct, status: "pending" },
        { key: "awaiting", label: "Awaiting Disbursement", count: l.awaiting_disbursement, pct: l.awaiting_disbursement_pct, status: "awaiting-disbursement" },
        { key: "active", label: "Active", count: l.active, pct: l.active_pct, status: "active" },
        { key: "overdue", label: "Overdue", count: l.overdue, pct: l.overdue_pct, status: "overdue" },
        { key: "completed", label: "Completed", count: l.completed, pct: l.completed_pct, status: "completed" }
      ];
    },
    refusalBoxes() {
      if (!this.dashboard) return [];
      const l = this.dashboard.lifecycle;
      return [
        { key: "refused", label: "Refused", count: l.refused, status: "refused" },
        { key: "reapply", label: "Refused & Reapply", count: l.refused_and_reapply, status: "refused-and-reapply" },
        { key: "r15", label: "Refused / 15 Days", count: l.refused_after_15_days, status: "refused-and-after-15-days" },
        { key: "r30", label: "Refused / 30 Days", count: l.refused_after_30_days, status: "refused-and-after-30-days" }
      ];
    },
    agingBuckets() {
      if (!this.dashboard) return [];
      const a = this.dashboard.overdue_aging;
      return [
        { key: "d1_7", label: "1–7 days", count: a.d1_7.count, amount: a.d1_7.amount, color: "#dc3545" },
        { key: "d8_30", label: "8–30 days", count: a.d8_30.count, amount: a.d8_30.amount, color: "#fd7e14" },
        { key: "d31_60", label: "31–60 days", count: a.d31_60.count, amount: a.d31_60.amount, color: "#ffc107" },
        { key: "d60_plus", label: "60+ days", count: a.d60_plus.count, amount: a.d60_plus.amount, color: "#842029" }
      ];
    }
  },
  methods: {
    formatMoney(value) {
      const n = Number(value || 0);
      return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-NG");
    },
    agingBarWidth(amount) {
      if (!this.dashboard) return 0;
      const max = Math.max(
        this.dashboard.overdue_aging.d1_7.amount,
        this.dashboard.overdue_aging.d8_30.amount,
        this.dashboard.overdue_aging.d31_60.amount,
        this.dashboard.overdue_aging.d60_plus.amount,
        1
      );
      return Math.max(Math.round((Number(amount || 0) / max) * 100), 4);
    },
    statusClass(status) {
      if (status === "active") return "is-active";
      if (status === "overdue") return "is-overdue";
      if (status === "completed") return "is-completed";
      if (status === "awaiting-disbursement") return "is-awaiting";
      if ((status || "").startsWith("refused")) return "is-refused";
      return "is-pending";
    },
    dueHint(item) {
      if (!item.due_date) return "";
      const days = Number(item.no_of_overdue_days || 0);
      if (item.loan_status === "overdue") {
        return `${Math.abs(days)} days overdue`;
      }
      if (item.loan_status === "active") {
        const remaining = Math.abs(days);
        return remaining ? `Due in ${remaining} days` : "Due today";
      }
      return "";
    },
    agingLabel(item) {
      if (item.loan_status !== "overdue") return "-";
      return `${Math.abs(Number(item.no_of_overdue_days || 0))}d`;
    },
    setRange(value) {
      this.range = value;
      this.customMode = false;
      this.refresh();
    },
    applyCustomRange() {
      if (!this.customFrom || !this.customTo) return;
      this.customMode = true;
      this.range = "custom";
      this.refresh();
    },
    applyKpiFilter(filter) {
      this.activeKpiFilter = filter;
      this.status = filter || "";
      this.loadLoans(1);
    },
    applyStatusFilter(status) {
      this.status = status || "";
      this.activeKpiFilter = status || "";
      this.loadLoans(1);
    },
    applyAgingFilter(bucket) {
      if (bucket === "d1_7") this.status = "overdue";
      else if (bucket === "d8_30") this.status = "overdue-by-3-days";
      else this.status = "overdue-by-7-days";
      this.activeKpiFilter = "overdue";
      this.loadLoans(1);
    },
    resetFilters() {
      this.search = "";
      this.status = "";
      this.activeKpiFilter = "";
      this.loadLoans(1);
    },
    async refresh() {
      await this.loadDashboard();
      this.loadLoans(1);
    },
    async loadDashboard() {
      this.dashboardLoading = true;
      try {
        let url = "loan/instant/nano-dashboard?";
        if (this.customMode && this.customFrom && this.customTo) {
          url += `from=${this.customFrom}&to=${this.customTo}`;
        } else {
          url += `range=${this.range}`;
        }
        const response = await ApiService.get(url);
        this.dashboard = response.data.data;
      } catch (error) {
        console.log(error && error.response);
        this.dashboard = null;
      } finally {
        this.dashboardLoading = false;
      }
    },
    async loadLoans(page = 1) {
      this.loansLoading = true;
      this.currentPage = page;
      try {
        const bounds = this.dashboard && this.dashboard.range ? this.dashboard.range : null;
        const status = this.status || "";
        let dateColumn = "created_at";
        if (status.indexOf("overdue") === 0) dateColumn = "due_date";
        else if (status === "active") dateColumn = "disbursed_date";
        const params = [
          `page=${page}`,
          `loan_status=${encodeURIComponent(this.status || "")}`,
          `instant_reference=${encodeURIComponent(this.search || "")}`,
          `phone=${encodeURIComponent(this.search || "")}`,
          `bvn=${encodeURIComponent(this.search || "")}`,
          "bypass=yes"
        ];
        if (bounds && bounds.from && bounds.to) {
          params.push(`start_date=${bounds.from}`);
          params.push(`end_date=${bounds.to}`);
          params.push(`date_column=${dateColumn}`);
        }
        const response = await ApiService.get(`loan/instant/all?${params.join("&")}`);
        const payload = response.data.data;
        this.loans = payload.data || [];
        this.currentPage = payload.current_page || 1;
        this.lastPage = payload.last_page || 1;
        this.loanCount = this.lastPage > 1
          ? `${this.loans.length}+`
          : this.loans.length;
      } catch (error) {
        console.log(error && error.response);
        this.loans = [];
      } finally {
        this.loansLoading = false;
      }
    },
    onPageChange(page) {
      if (page && this.currentPage !== page) {
        this.loadLoans(page);
      }
    }
  },
  mounted() {
    this.refresh();
  }
};
</script>

<style scoped>
.nano-dashboard {
  color: #1f2937;
}

.nano-toolbar {
  gap: 0.75rem;
}

.nano-custom-dates .form-control {
  width: auto;
  min-width: 140px;
}

.nano-search {
  min-width: 260px;
  max-width: 360px;
}

.nano-range-label {
  font-size: 0.85rem;
  color: #6b7280;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.45rem 0.75rem;
}

.nano-kpi-card {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 14px;
  padding: 1rem 1.1rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.nano-kpi-card:hover,
.nano-kpi-card.active {
  border-color: #93c5fd;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.08);
}

.nano-kpi-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.nano-kpi-value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.nano-kpi-sub {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.15rem;
}

.nano-kpi-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #f3f4f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nano-trend {
  font-size: 0.78rem;
  font-weight: 600;
}

.nano-trend.up {
  color: #15803d;
}

.nano-trend.down {
  color: #b91c1c;
}

.nano-panel {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
}

.nano-lifecycle {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.nano-life-pill {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.75rem;
  align-items: center;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  text-align: left;
}

.nano-life-pill .count {
  font-weight: 700;
}

.nano-life-pill .pct {
  color: #6b7280;
  font-size: 0.85rem;
}

.nano-refuse-box {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  text-align: left;
}

.nano-refuse-box .label {
  font-size: 0.75rem;
  color: #6b7280;
}

.nano-refuse-box .value {
  font-size: 1.15rem;
  font-weight: 700;
}

.nano-aging-row {
  cursor: pointer;
  margin-bottom: 0.85rem;
}

.nano-bar-track {
  height: 8px;
  background: #eef2f7;
  border-radius: 999px;
  overflow: hidden;
}

.nano-bar-fill {
  height: 100%;
  border-radius: 999px;
}

.nano-aging-stats .label {
  font-size: 0.75rem;
  color: #6b7280;
}

.nano-aging-stats .value {
  font-weight: 700;
}

.nano-table-filters {
  gap: 0.5rem;
}

.nano-table-filters .form-control {
  width: auto;
  min-width: 150px;
}

.nano-status {
  display: inline-block;
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.nano-status.is-active {
  background: #dcfce7;
  color: #166534;
}

.nano-status.is-overdue {
  background: #fee2e2;
  color: #991b1b;
}

.nano-status.is-completed {
  background: #ccfbf1;
  color: #115e59;
}

.nano-status.is-awaiting {
  background: #dbeafe;
  color: #1e40af;
}

.nano-status.is-pending {
  background: #f3f4f6;
  color: #374151;
}

.nano-status.is-refused {
  background: #e5e7eb;
  color: #4b5563;
}

@media (max-width: 768px) {
  .nano-search {
    min-width: 100%;
    max-width: 100%;
  }
}
</style>
